import React, { useState } from "react";
import { Edit, Delete } from "@mui/icons-material";
import { Box, Typography, IconButton, Chip, SxProps } from "@mui/material";

import Panel from "../../../../../components/Panel";
import { ILeaveType } from "../../../../../interfaces/LeaveInterface";
import OkhatiDialog from "../../../../../components/Dialog/Dialog";
import { useDeleteLeaveType } from "../../../../../hooks/query/useLeaveTypes";
import { LeaveAccrualFrequency, LeaveAccrualMethod } from "../../../../../constants/leave";

interface LeaveTypeInfoPanelProps {
  leave: ILeaveType;
  onClose: () => void;
  onEdit: (leave: ILeaveType) => void;
}

function DetailRow({ label, value, sx }: { label: string; value: React.ReactNode; sx?: SxProps }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline" }}>
      <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
        {label}
      </Typography>
      {typeof value === "string" || typeof value === "number" ? (
        <Typography sx={sx}>{value}</Typography>
      ) : (
        <Box sx={sx}>{value}</Box>
      )}
    </Box>
  );
}

function BooleanChip({ value }: { value?: boolean }) {
  return (
    <Chip
      size="small"
      label={value ? "Yes" : "No"}
      color={value ? "success" : "default"}
      variant="outlined"
      sx={{ height: 20, fontSize: "0.75rem" }}
    />
  );
}

// eslint-disable-next-line import/prefer-default-export
export function LeaveTypeInfoPanel({
  leave,
  onClose,
  onEdit
}: LeaveTypeInfoPanelProps): React.JSX.Element {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deleteMutation = useDeleteLeaveType();

  const handleDelete = async () => {
    deleteMutation.mutate(Number(leave.id), {
      onSuccess: (response) => {
        if (response.success) {
          onClose();
        }
      }
    });
  };

  const accrualPeriodLabel =
    leave.accrualFrequency === LeaveAccrualFrequency.MONTHLY ? "Monthly" : "Yearly";

  const accrualTypeLabel =
    leave.accrualMethod === LeaveAccrualMethod.LUMP_SUM ? "Lump Sum" : "Accrual";

  const titleJsx = (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      pb={1}
    >
      <Box display="flex" sx={{ alignItems: "center" }}>
        <Typography>
          <Box component="span" fontWeight="600" fontSize="16px" pr={2}>
            Leave Type Information
          </Box>
        </Typography>
      </Box>
      <Box display="flex">
        <IconButton
          sx={{ cursor: "pointer", padding: "5px" }}
          onClick={() => setDeleteDialogOpen(true)}
        >
          <Delete />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "baseline" }} ml="8px">
          <IconButton sx={{ cursor: "pointer", padding: "5px" }} onClick={() => onEdit(leave)}>
            <Edit />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Panel titleJsx={titleJsx} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <DetailRow label="Name" value={leave.name} />
        <DetailRow label="Code" value={leave.code} />
        <DetailRow label="Quota (Days)" value={leave.quota} />
        <DetailRow label="Accrual Period" value={accrualPeriodLabel} />
        {leave.accrualMethod && <DetailRow label="Accrual Method" value={accrualTypeLabel} />}
        <DetailRow label="Paid Leave" value={<BooleanChip value={leave.isPaid} />} />
        <DetailRow label="Prorated" value={<BooleanChip value={leave.isProrated} />} />
        <DetailRow label="Mappable" value={<BooleanChip value={leave.isMappable} />} />
        <DetailRow label="Encashable" value={<BooleanChip value={leave.isEncashable} />} />
        <DetailRow label="Carryable" value={<BooleanChip value={leave.isCarryable} />} />
        {leave.isCarryable && (
          <DetailRow label="Max Carryable Days" value={leave.maxCarryableDays ?? "-"} />
        )}
        <DetailRow label="Description" value={leave.description || "-"} />
      </Box>

      {deleteDialogOpen && (
        <OkhatiDialog
          title="Delete Leave"
          description={`Are you sure you want to delete the leave "${leave.name}"?`}
          next={handleDelete}
          cancel={() => setDeleteDialogOpen(false)}
          readMode={false}
        />
      )}
    </Panel>
  );
}
